import _ from 'lodash';
import { pipe } from 'ramda';
import { createSelector } from 'reselect';
import { localize, parse } from 'utils/apa/transformations';
import { getRegion } from './user';

export const getMetadata = state => _.get(state, 'metadata');

export const getTranslations = createSelector(
  getMetadata,
  metadataState => {
    const translations = _.get(metadataState, 'translations');

    try {
      return JSON.parse(translations);
    } catch (error) {
      console.error('Error parsing translations', error);
      return undefined;
    }
  },
);

export const selectMetadataKeyByRegion = (key, defaultValue = '{}') =>
  createSelector(
    [getMetadata, getRegion],
    (metadata, region) => {
      const propertyValue = metadata?.[key] || defaultValue;

      const processedProperty = pipe(
        parse,
        parsedObject => localize(parsedObject, region),
      )(propertyValue);

      return processedProperty;
    },
  );

export const getTranslationsByCountry = createSelector(
  [getTranslations, getRegion],
  (translations, region) => _.get(translations, ['language', region], {}),
);

export const getFilterList = createSelector(
  [getMetadata, getRegion],
  (metadataState, region) => {
    try {
      const configuration = JSON.parse(
        _.get(metadataState, 'byr_filterlist_configuration'),
      );
      return _.get(configuration, [region, 'filterlist']);
    } catch (error) {
      console.error('Error parsing filterlist configuration', error);
      return undefined;
    }
  },
);

export const getTalentProperties = createSelector(
  getMetadata,
  metadata => {
    const extMetadata = JSON.parse(
      _.get(metadata, ['external_metadata'], '{}'),
    );
    const order = JSON.parse(_.get(metadata, ['talent_role_priority'], '{}'));
    const provider =
      _.get(extMetadata, ['default', 'enabled']) ||
      _.get(extMetadata, ['external_metadata', 'default', 'enabled'])
        ? _.get(extMetadata, ['default', 'provider']) ||
          _.get(extMetadata, ['external_metadata', 'default', 'provider'])
        : 'amco';
    return { provider, order };
  },
);

export const getFooterParams = createSelector(
  [getMetadata],
  metadata => JSON.parse(_.get(metadata, ['footer_params'], '{}')),
);

export const getFooterSocial = createSelector(
  [getFooterParams, getTranslationsByCountry, getRegion],
  (footerParams, translations, region) => {
    const socials = _.get(
      footerParams,
      `socials.${region}.social`,
      _.get(footerParams, 'socials.default.social', []),
    );
    socials.map(a => {
      const url = _.get(a, 'url', false);
      if (url) a.url = _.get(translations, url, url);
      return a;
    });
    return socials;
  },
);

export const getFooterContact = createSelector(
  [getFooterParams, getTranslationsByCountry, getRegion],
  (footerParams, translations, region) => {
    const contact = _.get(
      footerParams,
      `contact.${region}`,
      _.get(footerParams, 'contact.default', {}),
    );
    _.forEach(contact, (a, b) => {
      if (b === 'url') return _.get(translations, a, a);
      return a;
    });
    return contact;
  },
);

export const getFooterChat = createSelector(
  [getFooterParams, getTranslationsByCountry, getRegion],
  (footerParams, translations, region) => {
    const chat = _.get(
      footerParams,
      `chat.${region}`,
      _.get(footerParams, 'chat.default', {}),
    );
    _.forEach(chat, (a, b) => {
      if (b === 'url') return _.get(translations, a, a);
      return a;
    });
    return chat;
  },
);

export const getFooterLinks = createSelector(
  [getFooterParams, getTranslationsByCountry, getRegion],
  (footerParams, translations, region) => {
    const links = _.get(
      footerParams,
      `links.${region}.link`,
      _.get(footerParams, 'links.default.link', []),
    );
    const left = [];
    const right = [];
    const items = [];
    links.map(a => {
      const url = _.get(a, 'url', false);
      const position = _.get(a, 'position', false);
      if (url) a.url = _.get(translations, url, url);
      if (position === 'left') return left.push(a);
      if (position === 'right') return right.push(a);
      return items.push(a);
    });

    if (items.length > 0) {
      const count = Math.round(items.length / 2);
      const chunks = _.chunk(items, count);
      _.merge(left, chunks[0]);
      if (chunks.length > 0) _.merge(right, chunks[1]);
    }
    return { left, right };
  },
);

export const getFooterInfo = createSelector(
  [getFooterSocial, getFooterContact, getFooterChat, getFooterLinks],
  (socials, contact, chat, links) => ({ socials, contact, chat, links }),
);
